import { Layout, Card, List, Avatar, Tag, Divider, Spin, message } from 'antd';
import { UserOutlined, CalendarOutlined, EnvironmentOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { useState, useEffect } from 'react';

const { Header, Content } = Layout;

// API configuration
const API_BASE_URL = process.env.REACT_APP_API_URL;

// Helper function to get status tag configuration
const getStatusConfig = (status) => {
  // status is a number: 0 = pending, 1 = started, 2 = ended
  switch (status) {
    case 0:
      return { color: 'orange', text: 'Pending' };
    case 1:
      return { color: 'green', text: 'Started' };
    case 2:
      return { color: 'default', text: 'Ended' };
    default:
      return { color: 'default', text: 'Unknown' };
  }
};

// Helper function to format date
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });
};

// Helper function to format time
const formatTime = (timeString) => {
  if (!timeString) return 'N/A';
  const [hours, minutes] = timeString.split(':');
  const hour = parseInt(hours);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${minutes} ${ampm}`;
};

// Helper function to format registered at timestamp
const formatRegisteredAt = (timestamp) => {
  if (!timestamp) return 'N/A';
  const date = new Date(timestamp);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
};

function EventCard({ event }) {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const statusConfig = getStatusConfig(event.status);

  // Fetch registered users when page or pageSize changes
  useEffect(() => {
    fetchRegistrations();
  }, [currentPage, pageSize]);

  const fetchRegistrations = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/events/${event.id}/registrations?page=${currentPage}&limit=${pageSize}`
      );
      const data = await response.json();
      
      if (data.success) {
        setRegistrations(data.data.users || []);
        setTotal(data.data.pagination?.total || 0);
      } else {
        message.error('Failed to fetch registrations');
      }
    } catch (error) {
      console.error('Error fetching registrations:', error);
      message.error('Failed to fetch registrations');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page, newPageSize) => {
    setCurrentPage(page);
    if (newPageSize !== pageSize) {
      setPageSize(newPageSize);
      setCurrentPage(1);
    }
  };

  return (
    <Card 
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: '20px', fontWeight: 600 }}>
            {event.title || event.name || 'Untitled Event'}
          </div>
          <Tag color={statusConfig.color} style={{ fontSize: '14px', padding: '4px 12px' }}>
            {statusConfig.text}
          </Tag>
        </div>
      }
      style={{ marginBottom: '16px', width: '100%' }}
    >
      <div style={{ marginBottom: '16px' }}>
        <p style={{ color: '#666', marginBottom: '16px', lineHeight: '1.6' }}>
          {event.description || 'No description available'}
        </p>
        
        <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
          <span>
            <CalendarOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
            {formatDate(event.event_date || event.date)}
          </span>
          <span>
            <ClockCircleOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
            {formatTime(event.event_time || event.time)}
          </span>
          <span>
            <EnvironmentOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
            {event.city || event.location || 'Location TBA'}
          </span>
          <Tag color="blue">{event.registered_count || 0} Registered</Tag>
        </div>
      </div>

      <Divider orientation="left">Registered Users</Divider>

      <Spin spinning={loading}>
        <List
          itemLayout="horizontal"
          dataSource={registrations}
          locale={{ emptyText: 'No registered users yet' }}
          pagination={total > 0 ? {
            current: currentPage,
            pageSize: pageSize,
            total: total,
            onChange: handlePageChange,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} users`,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            onShowSizeChange: handlePageChange
          } : false}
          renderItem={(user) => (
            <List.Item style={{ padding: '12px 0' }}>
              <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <Avatar 
                  icon={<UserOutlined />} 
                  src={user.photo }
                  style={{ backgroundColor: '#1890ff', flexShrink: 0 }}
                />
                <div style={{ marginLeft: '12px', flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 500, fontSize: '14px' }}>
                    {user.realname || 'Anonymous User'}
                  </div>
                  <div style={{ fontSize: '14px', color: '#666' }}>
                    {user.email || 'No email'}
                  </div>
                  <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
                    Registered at: {formatRegisteredAt(user.registered_at || user.created_at)}
                  </div>
                </div>
              </div>
            </List.Item>
          )}
        />
      </Spin>
    </Card>
  );
}

function App() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/events`);
      const data = await response.json();
      
      if (data.success) {
        // Handle both array and object with data property
        const eventsData = Array.isArray(data.data) ? data.data : [];
        setEvents(eventsData);
      } else {
        message.error('Failed to fetch events');
      }
    } catch (error) {
      console.error('Error fetching events:', error);
      message.error('Failed to connect to the server. Please make sure the backend is running.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header style={{ 
        background: '#fff', 
        padding: '0 24px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        display: 'flex',
        alignItems: 'center'
      }}>
        <img 
          src="/favicon.png" 
          alt="Logo" 
          style={{ 
            height: '32px', 
            marginRight: '16px' 
          }} 
        />
        <div style={{ fontSize: '20px', fontWeight: 600, color: '#333' }}>
          Muha events
        </div>
      </Header>
      <Content style={{ padding: '24px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h1 style={{ fontSize: '28px', fontWeight: 'bold', marginBottom: '24px' }}>
            Events & Registrations
          </h1>
          
          {loading ? (
            <div style={{ textAlign: 'center', padding: '50px' }}>
              <Spin size="large" />
            </div>
          ) : events.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '50px', color: '#999' }}>
              No events found
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {events.map((event) => (
                <EventCard key={event.id} event={event} />
              ))}
            </div>
          )}
        </div>
      </Content>
    </Layout>
  );
}

export default App;